// noinspection JSUnusedGlobalSymbols

import type { MapCameraChangedEvent } from '@vis.gl/react-google-maps';
import DOMPurify from 'isomorphic-dompurify';
import { capitalize } from 'lodash';

import type ZorgbaseResult from '@/lib/models/zorgbase/ZorgbaseResult';
import type { ObjectWithDistanceProperty } from './models/location';

export const isServerSideRendering = typeof window === 'undefined';

export function toRad(x: number) {
	return (x * Math.PI) / 180;
}

export type Point = Pick<GeolocationCoordinates, 'latitude' | 'longitude'>;

export type Position = {
	lat: number;
	lng: number;
};

// google.maps uses shorthand for lat/lng
export function pointToPosition(p: Point): Position | undefined {
	return p.latitude && p.longitude
		? {
				lat: p.latitude,
				lng: p.longitude,
			}
		: undefined;
}

export function positionToPoint(p: Position): Point | undefined {
	return p.lat && p.lng
		? {
				latitude: p.lat,
				longitude: p.lng,
			}
		: undefined;
}

export function haversineDistance(source: Point, target: Point): number {
	const dLat = Math.sin(toRad(target.latitude - source.latitude) / 2);
	const dLong = Math.sin(toRad(target.longitude - source.longitude) / 2);
	const a = dLat * dLat + Math.cos(toRad(source.latitude)) * Math.cos(toRad(target.latitude)) * dLong * dLong;
	const d = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return Number(d.toFixed(1));
}

export function sortByDistanceProperty(orgs: ObjectWithDistanceProperty[]): ObjectWithDistanceProperty[] {
	// sort the organisations by calculated distance
	return orgs.sort((a, b) => {
		if (!a.distance || !b.distance) {
			return 0;
		}
		return a.distance - b.distance;
	});
}

export function getDeviceLocation(callback: (location: Position) => void, defaultLocation: Position) {
	if (navigator.geolocation) {
		navigator.geolocation.getCurrentPosition(
			(position) =>
				callback({
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				}),
			(error) => {
				console.warn('[geolocation] geolocation unavailable, falling back to the default location', {
					code: error.code,
					message: error.message,
				});
				callback(defaultLocation);
			},
			{
				enableHighAccuracy: true,
				timeout: 20000,
				maximumAge: 0,
			},
		);
	} else {
		callback(defaultLocation);
	}
}

export function sanitizeMarkup(text?: string) {
	const raw = text ?? '';
	const sanitized = DOMPurify.sanitize(raw);
	return {
		__html: newlineToBreak(sanitized),
	};
}

export function markerColorForOrganisation(organisation: ZorgbaseResult) {
	if (organisation.acceptsPatients && organisation.acceptsPassersBy) {
		return '#ffffff';
	}
	if (organisation.acceptsPatients) {
		// return '#00ab42';
		return '#008533';
		// return '#006627';
	}
	if (organisation.acceptsPassersBy) {
		// return '#018dc6';
		return '#017CAD';
	}
	if (organisation.acceptsPatients === 0 && organisation.acceptsPassersBy === 0) {
		return '#fe4c35';
	}

	return '#bbb';
}

export function markerColorForAcceptsPatientsOrganisation(organisation: ZorgbaseResult) {
	if (organisation.acceptsPatients) {
		// return '#00ab42';
		return '#008533';
		// return '#006627';
	}
	if (organisation.acceptsPatients === 0) {
		return '#fe4c35';
	}
	// unknown
	return '#bbb';
}

export function newlineToBreak(value: string) {
	return value.replaceAll(/\n/g, '<br/>');
}

export function getMapKeyForValue(map: Map<number, string>, val: string): number | undefined {
	return [...map].find(([, value]) => val === value)?.[0];
}

export function getOrganisationDisplayName(organisation: ZorgbaseResult) {
	const parts = organisation.label.split(' ');
	parts.forEach((v, i, p) => {
		p[i] = capitalize(v);
	});
	return parts.join(' ');
}

export function isValidPostcode(postcode: string) {
	const postcodeRegex = /^[1-9][0-9]{3}\s?[A-Z]{0,2}$/;
	return postcodeRegex.test(postcode);
}

export function filterByBounds(
	items: Point[],
	{ north, south, east, west }: MapCameraChangedEvent['detail']['bounds'],
) {
	return items.filter((o) => {
		return o.latitude < north && o.latitude > south && o.longitude > west && o.longitude < east;
	});
}

export function calculateBounds(items: Point[]) {
	// { north, south, east, west }: MapCameraChangedEvent['detail']['bounds']
	let south = items[0].latitude,
		west = items[0].longitude,
		north = items[0].latitude,
		east = items[0].longitude;

	items.forEach((o) => {
		if (o.latitude > north) {
			north = o.latitude;
		}
		if (o.latitude < south) {
			south = o.latitude;
		}
		if (o.longitude < west) {
			west = o.longitude;
		}
		if (o.longitude > east) {
			east = o.longitude;
		}
	});
	return {
		south,
		west,
		north,
		east,
		padding: 10,
	};
}

export function countByType(items: ZorgbaseResult[]) {
	let open = 0;
	let closed = 0;
	let unknown = 0;

	items.forEach((i) => {
		switch (i.acceptsPatients) {
			case 1:
				++open;
				break;
			case 0:
				++closed;
				break;
			default:
				++unknown;
		}
	});

	return {
		open,
		closed,
		unknown,
	};
}

export function isDutchPostcode(value: string) {
	const postcodeRegex = /^[1-9][0-9]{3}\s?[A-Za-z]{0,2}$/;
	return !!postcodeRegex.test(value);
}
